// TESSERAX — HARMONIE PAR MONDE (cœur pur, testé).
//
// La musique générative (`scheduleStep` dans main.ts) bâtit, à chaque mesure, un
// accord sur une RACINE puisée dans une progression de 8 mesures, par-dessus laquelle
// court une mélodie pentatonique MINEURE (`PENT`) relative à la racine courante. La
// mélodie restant pentatonique-mineure relative à CHAQUE racine, elle demeure consonante
// quelle que soit la progression choisie : on a donc toute liberté de donner à chaque
// CHAMBRE (monde verrouillable, 0..5) sa propre tonalité et son propre contour mélodique.
//
// Avant cette passe, une UNIQUE progression (La mineur) sonnait dans les 6 chambres :
// six mondes visuellement distincts, sonorement identiques. On dote ici chaque monde
// d'un CENTRE TONAL et d'un contour propres → l'oreille RECONNAÎT la chambre où l'on joue
// (identité sonore par profondeur, cf. ROADMAP « Thème harmonique par monde »).
//
// Données pures : fréquences en Hz, aucune dépendance THREE / DOM / RNG. Le déterminisme
// du défi du jour est intact (l'audio n'altère ni le tirage ni le score).

// Gamme pentatonique MINEURE (demi-tons relatifs à la racine). Toujours consonante sur
// un accord mineur OU majeur bâti sur la même racine → sûr quelle que soit la progression.
export const PENT = [0, 3, 5, 7, 10] as const;

// Une progression de 8 mesures (racines en Hz) par MONDE, indexée comme STYLES/THEMES
// (0..5). Chaque entrée pose une tonalité distincte ET un contour distinct, accordés au
// climat visuel de la chambre. Toutes longueur 8, fréquences strictement positives
// (invariants verrouillés par le test) → `mBar % ROOTS.length` reste sûr partout.
export const ROOT_SETS: number[][] = [
  // 0 · Néon cyan (défaut) — LA mineur, contemplatif/classique (progression historique conservée).
  //     A  F  C  G  D  A  E  G
  [110.0, 87.31, 130.81, 98.0, 73.42, 110.0, 82.41, 98.0],
  // 1 · Émeraude/teal — MI mineur, ouvert et flottant (registre clair, élan ascendant).
  //     E  G  D  A  E  B  C  G
  [82.41, 98.0, 73.42, 110.0, 82.41, 123.47, 130.81, 98.0],
  // 2 · Magenta/rose — RÉ mineur, chaud et languissant (couleur dorienne, B♭ passager).
  //     D  F  A  G  D  B♭ C  A
  [73.42, 87.31, 110.0, 98.0, 73.42, 116.54, 130.81, 110.0],
  // 3 · Violet — DO mineur, profond et grave (registre bas, E♭/A♭/B♭ → gravité dramatique).
  //     C  E♭ G  F  C  A♭ B♭ G
  [65.41, 77.78, 98.0, 87.31, 65.41, 103.83, 116.54, 98.0],
  // 4 · Ambre/or — SOL majeur, lumineux et chaleureux (registre haut → éclat doré).
  //     G  B  D  C  G  D  E  C
  [98.0, 123.47, 146.83, 130.81, 98.0, 146.83, 164.81, 130.81],
  // 5 · Liquide/verre — FA lydien, aérien et cristallin (quartes ouvertes, miroitement).
  //     F  A  C  G  F  C  D  A
  [87.31, 110.0, 130.81, 98.0, 87.31, 130.81, 146.83, 110.0],
];

// Sélection sûre et bornée : tout index hors plage est ramené dans [0, ROOT_SETS.length)
// (modulo positif), garantissant qu'un appelant ne renvoie jamais `undefined`.
export function rootsForWorld(world: number): number[] {
  const n = ROOT_SETS.length;
  const i = (((world | 0) % n) + n) % n;
  return ROOT_SETS[i];
}
